import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions';
import Icon from './AppIcon';
import Button from './ui/Button';

const WaveformVisualizer = ({ audioUrl, height = 100, phonemeAnalysis = [], clarityScore = 0, previousAttempts = [] }) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const regionsPluginRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [hoveredRegion, setHoveredRegion] = useState(null);

  useEffect(() => {
    if (!waveformRef?.current || !audioUrl) return;

    // Initialize Regions plugin
    const regions = RegionsPlugin?.create();
    regionsPluginRef.current = regions;

    // Initialize WaveSurfer with Regions plugin
    const wavesurfer = WaveSurfer?.create({
      container: waveformRef?.current,
      waveColor: '#94a3b8',
      progressColor: '#3b82f6',
      cursorColor: '#ef4444',
      barWidth: 2,
      barRadius: 3,
      cursorWidth: 2,
      height: height,
      barGap: 2,
      responsive: true,
      normalize: true,
      plugins: [regions],
    });

    wavesurferRef.current = wavesurfer;

    // Load audio
    wavesurfer?.load(audioUrl);

    // Event listeners
    wavesurfer?.on('ready', () => {
      setIsReady(true);
      setDuration(wavesurfer?.getDuration());
      
      // Add color-coded regions for phoneme issues
      phonemeAnalysis?.forEach((phoneme, index) => {
        if (phoneme?.timestamp && phoneme?.duration) {
          const regionColor = phoneme?.severity === 'critical' ? 'rgba(239, 68, 68, 0.3)' :
                            phoneme?.severity === 'moderate' ? 'rgba(251, 191, 36, 0.3)' : 
                            'rgba(148, 163, 184, 0.2)';
          
          regions?.addRegion({
            start: phoneme?.timestamp,
            end: phoneme?.timestamp + phoneme?.duration,
            color: regionColor,
            drag: false,
            resize: false,
            content: phoneme?.word,
            data: {
              word: phoneme?.word,
              issue: phoneme?.issue,
              correction: phoneme?.correction,
              severity: phoneme?.severity
            }
          });
        }
      });
    });

    wavesurfer?.on('play', () => setIsPlaying(true));
    wavesurfer?.on('pause', () => setIsPlaying(false));
    wavesurfer?.on('finish', () => setIsPlaying(false));
    
    wavesurfer?.on('audioprocess', () => {
      setCurrentTime(wavesurfer?.getCurrentTime());
    });

    wavesurfer?.on('seek', () => {
      setCurrentTime(wavesurfer?.getCurrentTime());
    });

    // Region hover events
    regions?.on('region-in', (region) => {
      setHoveredRegion(region?.data);
    });

    regions?.on('region-out', () => {
      setHoveredRegion(null);
    });

    return () => {
      if (wavesurfer) {
        wavesurfer?.destroy();
      }
    };
  }, [audioUrl, height, phonemeAnalysis]);

  const handlePlayPause = () => {
    if (wavesurferRef?.current) {
      wavesurferRef?.current?.playPause();
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getClarityColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getClarityLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Work';
  };

  const getImprovementTrend = () => {
    if (previousAttempts?.length < 2) return null;
    
    const current = clarityScore;
    const previous = previousAttempts?.[previousAttempts?.length - 1]?.clarityScore || 0;
    const difference = current - previous;
    
    if (difference > 5) return { icon: 'TrendingUp', color: 'text-success', label: `+${difference}%` };
    if (difference < -5) return { icon: 'TrendingDown', color: 'text-destructive', label: `${difference}%` };
    return { icon: 'Minus', color: 'text-muted-foreground', label: 'Same' };
  };

  const improvementTrend = getImprovementTrend();

  return (
    <div className="w-full space-y-4">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Icon name="Activity" size={20} className="text-primary" />
            <h4 className="font-semibold text-foreground">Audio Waveform Analysis</h4>
          </div>

          {/* Clarity Score Badge */}
          {clarityScore > 0 && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Clarity Score</div>
                <div className={`text-2xl font-bold ${getClarityColor(clarityScore)}`}>
                  {clarityScore}%
                </div>
                <div className="text-xs text-muted-foreground">{getClarityLabel(clarityScore)}</div>
              </div>
              
              {/* Improvement Indicator */}
              {improvementTrend && (
                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${
                  improvementTrend?.color === 'text-success' ? 'bg-success/10 border-success/30' :
                  improvementTrend?.color === 'text-destructive'? 'bg-destructive/10 border-destructive/30' : 'bg-muted border-border'
                }`}>
                  <Icon name={improvementTrend?.icon} size={16} className={improvementTrend?.color} />
                  <span className={`text-sm font-semibold ${improvementTrend?.color}`}>
                    {improvementTrend?.label}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Waveform Container with Color Legend */}
        <div className="bg-background/50 rounded-lg p-4 mb-4 relative">
          <div ref={waveformRef} className="w-full" />
          
          {/* Hover Tooltip */}
          {hoveredRegion && (
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-background border border-border rounded-lg shadow-lg p-3 z-10 max-w-xs">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-bold text-sm text-foreground">{hoveredRegion?.word}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  hoveredRegion?.severity === 'critical' ? 'bg-destructive/20 text-destructive' :
                  hoveredRegion?.severity === 'moderate'? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'
                }`}>
                  {hoveredRegion?.severity}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-1">{hoveredRegion?.issue}</p>
              <p className="text-xs text-foreground">{hoveredRegion?.correction}</p>
            </div>
          )}

          {/* Color Legend */}
          {phonemeAnalysis?.length > 0 && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.5)' }} />
                <span className="text-xs text-muted-foreground">Critical Issues</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(251, 191, 36, 0.5)' }} />
                <span className="text-xs text-muted-foreground">Moderate Issues</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(148, 163, 184, 0.3)' }} />
                <span className="text-xs text-muted-foreground">Minor Issues</span>
              </div>
            </div>
          )}
        </div>

        {/* Playback Controls */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="md"
            iconName={isPlaying ? 'Pause' : 'Play'}
            iconPosition="left"
            onClick={handlePlayPause}
            disabled={!isReady}
          >
            {isPlaying ? 'Pause' : 'Play'}
          </Button>
          
          <div className="flex-1 flex items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono">{formatTime(currentTime)}</span>
            <span>/</span>
            <span className="font-mono">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Improvement Tracking */}
        {previousAttempts?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="BarChart3" size={18} className="text-primary" />
              <h5 className="text-sm font-semibold text-foreground">Your Progress</h5>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {previousAttempts?.slice(-4)?.map((attempt, index) => (
                <div 
                  key={index}
                  className="bg-muted/30 rounded-lg p-3 text-center"
                >
                  <div className="text-xs text-muted-foreground mb-1">
                    Attempt {previousAttempts?.length - previousAttempts?.slice(-4)?.length + index + 1}
                  </div>
                  <div className={`text-xl font-bold ${getClarityColor(attempt?.clarityScore)}`}>
                    {attempt?.clarityScore}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {attempt?.issueCount || 0} issues
                  </div>
                </div>
              ))}
              {/* Current Attempt */}
              <div className="bg-primary/10 border-2 border-primary rounded-lg p-3 text-center">
                <div className="text-xs text-primary font-semibold mb-1">Current</div>
                <div className={`text-xl font-bold ${getClarityColor(clarityScore)}`}>
                  {clarityScore}%
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {phonemeAnalysis?.length || 0} issues
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Visual indicators for phoneme issues */}
        {phonemeAnalysis?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="AlertTriangle" size={18} className="text-warning" />
              <h5 className="text-sm font-semibold text-foreground">Pronunciation Issues Detected</h5>
              <span className="ml-auto text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                Hover over colored regions on waveform for details
              </span>
            </div>
            <div className="space-y-2">
              {phonemeAnalysis?.slice(0, 3)?.map((analysis, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-2 p-3 rounded-lg border ${
                    analysis?.severity === 'critical' ? 'bg-destructive/10 border-destructive/30' :
                    analysis?.severity === 'moderate'? 'bg-warning/10 border-warning/30' : 'bg-muted/50 border-border'
                  }`}
                >
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    analysis?.severity === 'critical' ? 'bg-destructive' :
                    analysis?.severity === 'moderate'? 'bg-warning' : 'bg-muted-foreground'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-foreground">{analysis?.word}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        analysis?.severity === 'critical' ? 'bg-destructive/20 text-destructive' :
                        analysis?.severity === 'moderate'? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'
                      }`}>
                        {analysis?.severity}
                      </span>
                      {analysis?.timestamp && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          @ {formatTime(analysis?.timestamp)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{analysis?.issue}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaveformVisualizer;